interface LineChartProps {
  days: number;
  height?: number;
  showLegend?: boolean;
  data?: {
    joy: number;
    sadness: number;
    fear: number;
    anger: number;
    label: string;
  }[];
}

const joyData31 = [6,5,7,5,8,6,7,4,3,5,6,8,7,6,5,4,6,3,7,8,6,5,4,7,8,6,5,7,6,7,8];
const sadData31 = [3,4,2,4,2,3,2,6,5,4,3,2,3,4,4,6,3,5,2,2,3,4,5,3,2,4,5,3,4,3,2];
const fearData31 = [1,2,1,3,1,1,1,2,1,1,2,1,1,1,2,1,1,1,3,1,1,1,1,2,1,1,1,1,2,1,1];
const angerData31 = [1,1,2,1,1,2,1,2,1,4,1,1,1,2,1,1,1,2,1,1,1,3,1,1,2,1,1,2,1,1,1];

const joy7 = [5, 7, 6, 8, 4, 7, 8];
const sad7 = [4, 2, 4, 2, 6, 3, 2];
const fear7 = [1, 1, 2, 1, 2, 1, 1];
const anger7 = [2, 1, 3, 1, 2, 1, 1];

const EMOTIONS = [
  { key: 'joy', label: 'Радость', color: '#3ECFA0' },
  { key: 'sad', label: 'Печаль', color: '#4A90E2' },
  { key: 'fear', label: 'Страх', color: '#8B7AB8' },
  { key: 'anger', label: 'Гнев', color: '#FF6B6B' },
];

function buildPath(data: number[], w: number, h: number, max = 10): string {
  const n = data.length;
  const xs = data.map((_, i) => (i / (n - 1)) * w);
  const ys = data.map((v) => h - (v / max) * h);

  let d = `M ${xs[0]} ${ys[0]}`;
  for (let i = 1; i < n; i++) {
    const cp1x = xs[i - 1] + (xs[i] - xs[i - 1]) * 0.5;
    const cp1y = ys[i - 1];
    const cp2x = xs[i - 1] + (xs[i] - xs[i - 1]) * 0.5;
    const cp2y = ys[i];
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${xs[i]} ${ys[i]}`;
  }
  return d;
}

export default function LineChart({ days, height = 150, showLegend = true, data }: LineChartProps) {
  const W = 320;
  const H = height;

  const datasets = data
    ? [
        data.map((point) => point.joy),
        data.map((point) => point.sadness),
        data.map((point) => point.fear),
        data.map((point) => point.anger),
      ]
    : days === 31
    ? [joyData31, sadData31, fearData31, angerData31]
    : [joy7, sad7, fear7, anger7];

  const labels = data
    ? data.map((point) => point.label)
    : days === 31
    ? Array.from({ length: 31 }, (_, i) => (i + 1).toString())
    : ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  return (
    <div style={{ width: '100%' }}>
      <svg
        viewBox={`0 0 ${W} ${H + 20}`}
        style={{ width: '100%', height: `${H + 20}px`, overflow: 'visible' }}
        preserveAspectRatio="none"
      >
        {/* Horizontal grid lines */}
        {[2, 5, 8].map((v) => (
          <line
            key={v}
            x1={0} y1={H - (v / 10) * H}
            x2={W} y2={H - (v / 10) * H}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />
        ))}

        {/* Area fills */}
        {datasets.map((data, idx) => {
          const n = data.length;
          const xs = data.map((_, i) => (i / (n - 1)) * W);
          const ys = data.map((v) => H - (v / 10) * H);
          let areaPath = `M ${xs[0]} ${H}`;
          areaPath += ` L ${xs[0]} ${ys[0]}`;
          for (let i = 1; i < n; i++) {
            const cp1x = xs[i - 1] + (xs[i] - xs[i - 1]) * 0.5;
            const cp2x = xs[i - 1] + (xs[i] - xs[i - 1]) * 0.5;
            areaPath += ` C ${cp1x} ${ys[i - 1]}, ${cp2x} ${ys[i]}, ${xs[i]} ${ys[i]}`;
          }
          areaPath += ` L ${xs[n - 1]} ${H} Z`;
          return (
            <path
              key={`area-${idx}`}
              d={areaPath}
              fill={EMOTIONS[idx].color}
              fillOpacity="0.06"
            />
          );
        })}

        {/* Lines */}
        {datasets.map((data, idx) => (
          <path
            key={`line-${idx}`}
            d={buildPath(data, W, H)}
            fill="none"
            stroke={EMOTIONS[idx].color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {/* X axis labels */}
        {labels.map((label, i) => {
          const skip = days === 31 ? (i % 5 !== 0 && i !== 30) : false;
          if (skip) return null;
          const x = (i / (labels.length - 1)) * W;
          return (
            <text
              key={`label-${i}`}
              x={x}
              y={H + 16}
              textAnchor="middle"
              fontSize="9"
              fill="#6B7A94"
            >
              {label}
            </text>
          );
        })}
      </svg>

      {showLegend && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', marginTop: '8px', flexWrap: 'wrap' }}>
          {EMOTIONS.map((e) => (
            <div key={e.key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: e.color }} />
              <span style={{ fontSize: '11px', color: '#A8B2C8' }}>{e.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
